import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { seedKnowledgeGraph, getKnowledgeGraphStats } from "@/lib/kg.seed.functions";

export const Route = createFileRoute("/admin/kg")({
  component: KGAdmin,
});

function KGAdmin() {
  const seed = useServerFn(seedKnowledgeGraph);
  const stats = useServerFn(getKnowledgeGraphStats);
  const [busy, setBusy] = useState(false);
  const [output, setOutput] = useState<string>("");

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setOutput("Running…");
    try {
      const res = await fn();
      setOutput(JSON.stringify(res, null, 2));
    } catch (e) {
      setOutput(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="overline mb-2">Maintenance</p>
        <h2 className="font-serif text-2xl text-primary">Knowledge graph seed &amp; embeddings</h2>
        <p className="text-ink-secondary text-sm mt-2 max-w-2xl">
          Idempotent. Re-running &quot;Seed&quot; only re-embeds rows whose body changed.
          &quot;Force re-embed all&quot; ignores the diff and re-embeds every seed node — use
          after changing the embedding model.
        </p>
      </div>

      <div className="border border-border bg-card p-6 space-y-4">
        <div className="flex gap-3 flex-wrap">
          <button
            disabled={busy}
            onClick={() => run(() => stats())}
            className="px-4 py-2 border border-border text-sm hover:bg-muted disabled:opacity-50"
          >
            Get stats
          </button>
          <button
            disabled={busy}
            onClick={() => run(() => seed({ data: {} }))}
            className="px-4 py-2 bg-primary text-primary-foreground text-sm hover:opacity-90 disabled:opacity-50"
          >
            Seed (idempotent)
          </button>
          <button
            disabled={busy}
            onClick={() => run(() => seed({ data: { force: true } }))}
            className="px-4 py-2 border border-border text-sm hover:bg-muted disabled:opacity-50"
          >
            Force re-embed all
          </button>
        </div>
        {output && (
          <pre className="text-xs bg-muted p-4 overflow-auto max-h-96 whitespace-pre-wrap font-mono">
            {output}
          </pre>
        )}
      </div>
    </div>
  );
}
